import { EllipsisVertical, Pencil } from 'lucide-react'

import type { OrganizationLighthouse, OrganizationVisibility } from '@ankor-io/common/types/account'
import {
  Badge,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  Link,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

export type OrganizationsTableProps = {
  organizations: OrganizationLighthouse[]
  isLoading: boolean
}

export function OrganizationsTable({ organizations, isLoading = false }: OrganizationsTableProps) {
  const getVisibilityBadge = (visibility: OrganizationVisibility) => {
    if (visibility === 'visible') {
      return <Badge color="green">Visible</Badge>
    }
    return <Badge color="gray">Hidden</Badge>
  }

  return (
    <div className="overflow-x-auto">
      <Table className="max-w-[calc(100vw-2rem)] lg:max-w-[calc(100vw-20rem)]">
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Users</TableHeader>
            <TableHeader>Visibility</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center py-12"
              >
                <div className="flex items-center justify-center">
                  <Loading />
                </div>
              </TableCell>
            </TableRow>
          ) : (
            organizations.map((org, index) => (
              <TableRow
                key={`${org.id}-${index}`}
                className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                to={`/organizations/${org.id}`}
                title={`View ${org.name} organization details`}
              >
                <TableCell>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-50">{org.name}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{org.description}</span>
                </TableCell>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">{org.userCount}</TableCell>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                  {getVisibilityBadge(org.visibility)}
                </TableCell>
                <TableCell>
                  <div
                    className="relative z-10 pointer-events-auto"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Dropdown>
                      <DropdownButton plain>
                        <EllipsisVertical data-slot="icon" />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        <DropdownItem>
                          <Pencil data-slot="icon" />
                          <Link
                            to={`/organizations/${org.id}`}
                            className="text-sm text-gray-900 dark:text-gray-100"
                          >
                            Edit
                          </Link>
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
